import {Component, inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {Subscription} from 'rxjs';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {SharedModule} from '../../shared.module';
import {MatGridListModule} from '@angular/material/grid-list';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {provideNativeDateAdapter} from '@angular/material/core';
import {GridFilter} from '../../model/page-grid';
import {MatRadioModule} from '@angular/material/radio';
import clearFormFields from "src/infrastructure/helper/clear-form-fields";
import {MatSelectModule} from '@angular/material/select';
import {AsyncPipe, CommonModule} from '@angular/common';
import {DynamicAutocompleteComponent} from '../dynamic-autocomplete/dynamic-autocomplete.component';
import {ReconciliationAutocompleteComponent} from '../../../payments/components/reconciliation-autocomplete/reconciliation-autocomplete.component';
import {MatSnackBar} from "@angular/material/snack-bar";
import {GridFilterService} from "../../service/grid-filter.service";

@AutoUnsubscribe()
@Component({
    selector: 'page-grid-filter',
    templateUrl: './page-grid-filter.component.html',
    providers: [provideNativeDateAdapter()],
    imports: [
        CommonModule,
        SharedModule,
        FormsModule,
        ReactiveFormsModule,
        MatGridListModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatRadioModule,
        MatSelectModule,
        AsyncPipe,
        DynamicAutocompleteComponent,
        ReconciliationAutocompleteComponent,
    ]
})
export class PageGridFilterComponent implements OnInit {

    private dialogRef: MatDialogRef<PageGridFilterComponent> = inject(MatDialogRef<PageGridFilterComponent>);
    protected gridFilters = inject<{filters: GridFilter[], currentValue: any, singleColumn: boolean, dataTestId: string}>(MAT_DIALOG_DATA);
    private formBuilder = inject(FormBuilder);
    private breakpointObserver = inject(BreakpointObserver);
    private readonly gridFilterService = inject(GridFilterService);

    protected filterForm!: FormGroup;

    breakPoint$?: Subscription;
    isSmall: boolean = false;

    constructor(private readonly _snackBar: MatSnackBar,) {

        let filters: Record<string, null> = {};

        this.gridFilters.filters.forEach((filter)=>{
            if(filter.filterType === 'DATERANGE' && filter.lastFilterProperty) {
                filters[filter.filterProperty] = null;
                filters[filter.lastFilterProperty] = null;
            } else if (filter.filterResetValue !== undefined) {
              filters[filter.filterProperty] = filter.filterResetValue;
            }
            else {
              filters[filter.filterProperty] = null;
            }
        });
        this.filterForm = this.formBuilder.group(filters);
        this.filterForm.valueChanges.subscribe(value => this.gridFilterService.filters.set(value));

        this.gridFilters.filters.filter((item)=> !!item.validators).forEach((item)=>{
            this.filterForm.get(item.filterProperty)?.setValidators(item.validators ?? []);
            if(item.lastFilterProperty) {
                this.filterForm.get(item.lastFilterProperty)?.setValidators(item.validators ?? []);
            }
        });

        this.breakPoint$ = this.breakpointObserver.observe([
            Breakpoints.Medium,
            Breakpoints.Small,
            Breakpoints.XSmall
        ]).subscribe(result => {
            this.isSmall = result.matches;
        });

    }

    ngOnInit(): void {
        this.setFields();

        if (this.gridFilters.currentValue) {
            const currentValues = { ...this.gridFilters.currentValue };
            this.gridFilters.filters
                .filter(filter => filter.filterType === 'AUTOCOMPLETE')
                .forEach(filter => {
                    const value = currentValues[filter.filterProperty];
                    if (value) {
                        if (typeof value === 'string') {
                            currentValues[filter.filterProperty] = value;
                        } else if (typeof value === 'object' && value.value) {
                            currentValues[filter.filterProperty] = value.display || value.value || value.name || value.text || value;
                        }
                    }
                });

            this.filterForm.patchValue(currentValues);
        }
    }

    private validateDates(filter: GridFilter) {
      if (!filter.validateDates?.propertyFrom || !filter.validateDates?.propertyTo) return;
      const dateFrom = this.filterForm.value[filter.validateDates.propertyFrom];
      const dateTo = this.filterForm.value[filter.validateDates.propertyTo];
      if (!dateFrom) return;
      if (dateTo && dateFrom > dateTo) this.filterForm.get(filter.validateDates.propertyTo)?.setValue(dateFrom);
    }

    protected getMinDateValue(filter: GridFilter): Date | null {
      if (!filter.validateDates?.propertyFrom || filter.filterProperty !== filter.validateDates.propertyTo) return null;
      const dateFrom = this.filterForm.value[filter.validateDates.propertyFrom];
      if (!dateFrom) return null;
      return new Date(dateFrom);
    }

    protected onBlur(filter: GridFilter): void {
      if (!!filter.validateDates) this.validateDates(filter);
      if (!filter.dependsOn) return;
      const {property, errorMessage} = filter.dependsOn;
      if (this.filterForm.value[property] || !this.filterForm.value[filter.filterProperty]) return;
      this._snackBar.open(errorMessage, 'Dismiss', {
        duration: 0,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['message-position', 'app-notification-error']
      });
    }
    protected applyFilters(): void {
        const filterFormValues = { ...this.filterForm.value };

        for (let filterName in filterFormValues) {
            if (typeof filterFormValues[filterName] === 'string') {
                filterFormValues[filterName] = filterFormValues[filterName].trim();
            }

            const formControl = this.filterForm.get(filterName);
            if (formControl?.errors) {

                const isAutocompleteField = this.gridFilters.filters.some(f =>
                    f.filterProperty === filterName && f.filterType === 'AUTOCOMPLETE'
                );

                if (isAutocompleteField && filterFormValues[filterName] &&
                    typeof filterFormValues[filterName] === 'object') {
                } else {
                    formControl.reset();
                    filterFormValues[filterName] = null;
                }
            }
        }

        const searchDependsOn = this.gridFilters.filters.find(element => !!element.dependsOn);
        if (searchDependsOn && searchDependsOn.dependsOn) {
          const {property} = searchDependsOn.dependsOn;
          if (filterFormValues[property] && !filterFormValues[searchDependsOn.filterProperty]) {
            this.filterForm.get(searchDependsOn.filterProperty)?.setValue(filterFormValues[property]);
            filterFormValues[searchDependsOn.filterProperty] = filterFormValues[property];
          }
          if (!filterFormValues[property] && filterFormValues[searchDependsOn.filterProperty]) {
            this.onBlur(searchDependsOn);
            return;
          }
        }

        this.gridFilters.filters
          .filter(filter => filter.filterType === 'AUTOCOMPLETE')
          .forEach(filter => {
            const value = filterFormValues[filter.filterProperty];

            if (typeof value === 'string' && value.trim()) {
              if (!filter.config?.autocompleteEmitText) {
                filterFormValues[filter.filterProperty] = { value: value.trim() };
              } else {
                filterFormValues[filter.filterProperty] = value.trim();
              }
            }
            else if (value && typeof value === 'object') {
              const isClientProfileName = filter.config?.autocompleteTableName?.toLowerCase() === 'client' && filter.config?.autocompleteColumnName?.toLowerCase() === 'profilename';

              if (isClientProfileName && value.originalValue) {
                filterFormValues[filter.filterProperty] = {
                  ...value,
                  value: value.originalValue
                };
              }
            }
        });

        const meaningfulFilters: any = {};
        let hasMeaningfulFilters = false;

        for (const [key, value] of Object.entries(filterFormValues)) {
            const isRadioButtonFilter = this.gridFilters.filters.some(f =>
                f.filterProperty === key && f.filterType === 'RADIOBUTTON'
            );

            if (isRadioButtonFilter) {
                meaningfulFilters[key] = value;
                if (value !== null) {
                    hasMeaningfulFilters = true;
                }
            } else if (value !== null && value !== undefined && value !== '') {
                if (typeof value === 'string' && value.trim() !== '') {
                    meaningfulFilters[key] = value.trim();
                    hasMeaningfulFilters = true;
                } else if (typeof value === 'object' && value instanceof Date) {
                    meaningfulFilters[key] = value;
                    hasMeaningfulFilters = true;
                } else if (typeof value === 'object' && value &&
                           (('value' in value && (value as any).value) ||
                            ('id' in value && (value as any).id))) {
                    meaningfulFilters[key] = value;
                    hasMeaningfulFilters = true;
                } else if (typeof value !== 'object' && value !== '') {
                    meaningfulFilters[key] = value;
                    hasMeaningfulFilters = true;
                }
            }
        }

        this.closeFilter(hasMeaningfulFilters ? meaningfulFilters : null);
    }

    protected closeFilter(filter?:any): void {
        this.dialogRef.close(filter);
    }

    protected clearFields(...args:string[]): void {
        clearFormFields(this.filterForm, ...args);
    }

    protected setFields(): void {
        const customFiltersSet = this.gridFilters.filters.filter((filter)=> !!filter.filterSetValue);

        if(customFiltersSet) {
            customFiltersSet.forEach((filter)=>{
                this.filterForm.get(filter.filterProperty)
                    ?.patchValue(filter?.filterSetValue)
            });
        }
    }

    protected resetFields(): void {
        const fieldsToPreserve = ['asOfDate'];
        const allFields = Object.keys(this.filterForm.controls);
        const fieldsToClear = allFields.filter(field => !fieldsToPreserve.includes(field));
        
        clearFormFields(this.filterForm, ...fieldsToClear);

        const customFiltersReset = this.gridFilters.filters.filter((filter)=> !!filter.filterResetValue && !fieldsToPreserve.includes(filter.filterProperty));
        if(customFiltersReset) {
            customFiltersReset.forEach((filter)=>{
                this.filterForm.get(filter.filterProperty)
                    ?.patchValue(filter?.filterResetValue)
            });
        }
    }

    protected trackByFilter(_index: number, filter: GridFilter): string {
        return filter.filterProperty + '_' + (filter.config?.autocompleteTableName || '') + '_' + (filter.config?.autocompleteColumnName || '');
    }

    protected setColumn(): number {
        if(this.gridFilters.singleColumn) {
            return 1;
        }

        return this.isSmall ? 1 : 2
    }

}
